import { FC, Fragment, useState } from "react";
import { observer } from "mobx-react";
import { isFailure, isOk, isPending } from "@/common/common";
import { Alert } from "@/components-v2/Alert";
import { Box, VBox } from "@/components-v2/Box";
import { DateInput } from "@/components-v2/DateInput";
import { Button, CloseButton } from "@/components-v2/Button";
import { Dialog } from "@/components-v2/Dialog";
import { Spinner } from "@/components-v2/LoadingIndicator";
import { TMessage } from "@/i18n/tt";
import { useDispatch, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { EntityDataImportViewProps, prepEntityDataImportView } from "./entityDataImportDomain";
import {
  ImportGrid,
  SpreadsheetImportFilters,
  SpreadsheetImportResultDialog,
} from "./spreadsheetImportComponents";

export const EntityDataImportDialog: FC = observer(() => {
  const dispatch = useDispatch();
  const state = useRootStore().entityDataImport;
  const props = prepEntityDataImportView(state, dispatch);
  if (!props) {
    return null;
  }
  return props.result ? (
    <SpreadsheetImportResultDialog result={props.result} onClose={props.onClose} />
  ) : (
    <EntityDataImportView {...props} />
  );
});

const EntityDataImportView: FC<EntityDataImportViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <Dialog
      fullscreen
      actions={<Fragment />}
      subHeaderElement={
        <VBox>
          <Box paddingX={"md"} paddingY={"sm"} gap={"md"} alignItems={"center"} borderBottom>
            <CloseButton onClick={props.onClose} />
            <span>{t("entity-data.spreadsheet-title")}</span>
          </Box>
          {isOk(props.setupResult) && <Toolbar {...props} />}
        </VBox>
      }
    >
      <VBox gap={"md"} grow overflow={"hidden"}>
        {isPending(props.setupResult) && (
          <Box alignItems={"center"} gap={"sm"}>
            <Spinner />
            {t("entity-data.spreadsheet-reading")}
          </Box>
        )}
        {isFailure(props.setupResult) && (
          <Alert type={"error"} message={t(props.setupResult.error)} />
        )}
        {props.importFailed && <Alert type={"error"} message={t("general-error")} />}
        {isOk(props.setupResult) && <ImportGrid {...props} />}
      </VBox>
    </Dialog>
  );
});

const Toolbar: FC<EntityDataImportViewProps> = observer((props) => {
  const { t } = useTranslation();
  return (
    <Box paddingX={"md"} paddingY={"sm"} gap={"lg"} alignItems={"center"} wrap>
      <SpreadsheetImportFilters
        {...props}
        rowsLabel={{ key: "entity-data.spreadsheet-companies" }}
      />
      <Box marginLeft={"auto"} alignItems={"center"} gap={"md"}>
        <Box tooltip={{ key: "entity-data.spreadsheet-effective-date-help" }}>
          {t("entity-data.effective-date")}
        </Box>
        <EffectiveDateInput
          value={props.effectiveDate}
          issue={props.effectiveDateIssue}
          maxDate={props.maxEffectiveDate}
          onChange={props.onChangeEffectiveDate}
        />
        <Button
          variant={"primary"}
          label={{ key: "entity-data.spreadsheet-import" }}
          disabled={!props.canImport}
          loading={props.isImporting}
          onClick={props.onImport}
        />
      </Box>
    </Box>
  );
});

const EffectiveDateInput: FC<{
  value: string;
  issue?: TMessage;
  maxDate: string;
  onChange: (value: string) => void;
}> = ({ value, issue, maxDate, onChange }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);
  return (
    <DateInput
      value={draft}
      error={issue !== undefined}
      message={issue && t(issue)}
      maxDate={maxDate}
      placeholder={t("entity-data.spreadsheet-choose-date")}
      onInput={(event) => setDraft(event.target.value)}
      onChange={(params) => {
        setDraft(params.input);
        onChange(params.input);
      }}
    />
  );
};
